<template>
  <div class="tw-relative tw-z-0 tw-basis-0 tw-grow tw-shrink">
    <div class="tw-flex tw-flex-col tw-h-full">
      <div>
        <!-- Empty state + sidepane comms -->
        <SidepaneCommunicationEmptySlim
          v-if="
            showEmptyState && (shouldRenderEmptyState || allRemarksFilteredOut)
          "
        />
        <SidepaneCommunicationEmpty
          v-else-if="shouldRenderEmptyState"
          :cards-filter="typeFilter"
          :empty-translation-key="emptyTranslationKey"
        />
        <div
          v-else
          class="tw-flex tw-flex-col tw-grow tw-gap-4 tw-px-6 tw-py-4"
        >
          <RemarkCard
            v-for="remark in sortedOpenRemarks"
            :key="remark.id"
            :remark="remark"
            :show-context="!hasContext"
            :enable-element-icon-scroll="enableElementIconScroll"
            show-tags
          ></RemarkCard>
          <SfAssistantChecks
            v-if="showSfAssistantSidepaneChecksExpanded"
            :remarks="filteredSfAssistantRemarks"
            :expanded="true"
            :only-todos="true"
            :done="true"
          />

          <RemarkGroup
            v-if="sortedCompletedTodos.length > 0"
            :data-teststate-persisted="completedTodosPreferencePersisted"
            :show-mark-as-read="false"
            :disable-mark-as-read="true"
            :initially-opened="completedTodosOpen"
            :name="
              I18n.t('communication.todos.completed_todos', {
                count: sortedCompletedTodos.length,
              })
            "
            @toggled="persistSidepaneOpenCompletedTodosState"
          >
            <RemarkCard
              v-for="remark in sortedCompletedTodos"
              :key="remark.id"
              :remark="remark"
              :show-context="!hasContext"
              :enable-element-icon-scroll="enableElementIconScroll"
              show-tags
            ></RemarkCard>
          </RemarkGroup>

          <div
            v-if="hiddenRemarksCount > 0 && workflowReviewActiveTag"
            :class="{ 'sf-pad-t-4': allRemarksFilteredOut }"
            class="sf-row sf-row-center sf-align-c filtered-out sf-below-3"
          >
            <div
              :class="{
                'sf-font-bold sf-font-m sf-c-text-dark': allRemarksFilteredOut,
              }"
              class="sf-below-1"
            >
              {{
                I18n.t('communication.filtered_out_message', {
                  count: hiddenRemarksCount,
                  name: workflowReviewActiveTag.name,
                })
              }}
            </div>
            <div
              v-dompurify-html="I18n.t('communication.remove_filter_message')"
              class="remove-tag-filter sf-c-text"
              @click="removeTagFilter"
            ></div>
          </div>

          <SfAssistantChecks
            v-if="showSfAssistantSidepaneChecks"
            :remarks="filteredSfAssistantRemarks"
            :expanded="false"
          />

          <SfAssistantChecks
            v-if="showSfAssistantSidepaneChecksExpanded"
            :remarks="filteredSfAssistantRemarks"
            :expanded="true"
            :only-todos="true"
            :done="false"
          />

          <CheckList
            v-for="checkList in sortedPendingCheckLists"
            :key="checkList.id"
            ref="checkList"
            v-bind="checkList"
          />
          <div v-show="sortedCompletedCheckLists.length">
            <div class="sf-align-c">
              <SfButtonDs size="sm" @click="toggleCompletedCheckLists">{{
                I18n.t(
                  'communication.checklists.toggle_completed.' +
                    showingCompletedCheckLists,
                  { count: sortedCompletedCheckLists.length }
                )
              }}</SfButtonDs>
            </div>
          </div>
          <CheckList
            v-for="checkList in sortedCompletedCheckLists"
            v-show="showingCompletedCheckLists"
            :key="checkList.id"
            ref="checkList"
            v-bind="checkList"
          />

          <!-- Loading spinner -->
          <template v-if="fetchingCommunicationsInProgress">
            <div class="sf-align-c" data-testid="sidepane-loading-spinner">
              <div class="sf-spinner sf-inline-block"></div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import merge from 'lodash/merge'
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia'
import { mapActions, mapGetters, mapState } from 'vuex'

import CheckList from './checks/check-list'
import scrollHelper from './mixins/scroll-helper'
import SidepaneCommunicationEmpty from './sidepane-communication-empty'
import SidepaneCommunicationEmptySlim from './sidepane-communication-empty-slim'
import { SfLog } from '@app/adapters/logger'
import useCheckHelpers from '@app/application/communication/sections/communication/composables/use-check-helpers'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import SfAssistantChecksSidepaneList from '@app/application/sf-assistant-checks/components/sf-assistant-sidepane-list'
import {
  doneChecks,
  showSfAssistantChecks,
} from '@app/application/sf-assistant-checks/utils'
import SfHeightWatcher from '@app/shared/vue/components/sf-height-watcher.vue'
import RemarkCard from '@app/shared/vue/components/molecules/remark/remark-card.vue'
import {
  createEvent,
  getRightSidebarScrollOffset,
  isVisible,
  rightDrawerSidepaneContainerSelector,
} from '@app/utils/domHelpers'

import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { useCommsFilterStore } from '@app/application/communication/store/comms-filters'
import { useAuditsStore } from '@app/application/sidebar-audit-log/store/audits'
import RemarkGroup from '@app/application/communication/screens/inbox/components/remark-group.vue'
import { persistSidepaneOpenCompletedTodosState } from '@app/shared/api/drawer'
import useHeightObserver from '@app/shared/vue/composables/use-height-observer'
import waitForSidepaneAnimationEnd from '@app/application/communication/sections/communication/utils/wait-for-sidepane-animation-end'

const SidepaneCommunication = {
  components: {
    SfButtonDs,
    RemarkGroup,
    RemarkCard,
    CheckList,
    SidepaneCommunicationEmpty,
    SidepaneCommunicationEmptySlim,
    SfAssistantChecks: SfAssistantChecksSidepaneList,
    SfHeightWatcher,
  },
  mixins: [scrollHelper],
  props: {
    emptyTranslationKey: {
      type: String,
      default: 'communication.empty_state',
    },
    showEmptyState: {
      type: Boolean,
      default: false,
    },
    enableElementIconScroll: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    useHeightObserver()
    return {
      ...useCheckHelpers(),
      completedTodosOpen:
        !!window.Silverfin.currentUserPreferences.sidepane_open_completed_todos,
    }
  },

  data() {
    return {
      anchorElementScrolled: false,
      showSfAssistantChecksPane: false,
      completedTodosPreferencePersisted: false,
    }
  },

  computed: {
    ...mapGetters([
      'fetchingCommunicationsInProgress',
      'hasContext',
      'notes',
      'todos',
      'checkListChecks',
      'filteredNotes',
      'filteredTodos',
      'filteredDoneTodos',
      'filteredOpenTodos',
      'filteredSfAssistantRemarks',
      'filteredCheckLists',
      'workflowReviewActiveTag',
    ]),

    ...mapState({
      ledgerId: (state) => state.ledgerId,
      storeLedgerId: (state) => state.ledgerId,
      showingCompletedTodos: (state) => state.ui.showingCompletedTodos,
      showingCompletedCheckLists: (state) =>
        state.ui.showingCompletedCheckLists,
    }),

    ...mapPiniaState(useChecksStore, ['checks', 'hasChecks']),
    ...mapPiniaState(useCommsFilterStore, ['typeFilter']),
    ...mapPiniaState(useAuditsStore, ['changesSinceLastVisit']),

    showSfAssistantSidepaneChecks() {
      return (
        (this.typeFilter === null || this.typeFilter === 'assistant') &&
        this.showSfAssistantChecksPane
      )
    },

    showSfAssistantSidepaneChecksExpanded() {
      return this.typeFilter === 'todo' && this.showSfAssistantChecksPane
    },

    showChangesSinceLastVisit() {
      return this.changesSinceLastVisit.count > 0
    },

    sortedOpenRemarks() {
      return this.sortRemarks(
        merge({}, this.filteredNotes, this.filteredOpenTodos)
      )
    },

    sortedNotes() {
      return this.sortRemarks(this.filteredNotes)
    },

    sortedTodos() {
      return this.sortRemarks(this.filteredTodos)
    },

    sortedCompletedTodos() {
      return this.sortedTodos.filter((todo) => todo.done)
    },

    sortedPendingTodos() {
      return _.without(this.sortedTodos, ...this.sortedCompletedTodos)
    },

    sortedCheckLists() {
      return _.orderBy(this.filteredCheckLists, ['text'], ['asc'])
    },

    sortedCompletedCheckLists() {
      return this.sortedCheckLists.filter((checkList) =>
        this.checkListChecks(this.ledgerId, checkList.id).every((check) => {
          return this.checkCheckedByAllParticipants(check)
        })
      )
    },

    sortedPendingCheckLists() {
      return _.without(this.sortedCheckLists, ...this.sortedCompletedCheckLists)
    },

    checkIds() {
      return _.flatMap(this.$refs.checkList, (checkList) => checkList.checkIds)
    },

    isEmpty() {
      if (this.typeFilter) return false
      if (this.showChangesSinceLastVisit) return false

      return (
        this.hiddenRemarksCount === 0 &&
        this.sortedTodos.length === 0 &&
        this.sortedNotes.length === 0 &&
        this.sortedCheckLists.length === 0 &&
        !this.hasChecks
      )
    },

    allRemarksFilteredOut() {
      return this.hiddenRemarksCount === this.todosCount + this.notesCount
    },

    hiddenRemarksCount() {
      const hiddenNotesCount =
        this.notesCount - Object.keys(this.filteredNotes).length
      const hiddenTodosCount =
        this.todosCount - Object.keys(this.filteredTodos).length

      return hiddenNotesCount + hiddenTodosCount
    },

    notesCount() {
      return Object.keys(this.notes).length
    },
    todosCount() {
      return Object.keys(this.todos).length
    },
    shouldRenderEmptyState() {
      return !this.fetchingCommunicationsInProgress && this.isEmpty
    },
    completedSfAssistantTodosCount() {
      return this.typeFilter === 'todo'
        ? doneChecks(this.checks, this.filteredSfAssistantRemarks).length
        : 0
    },
  },

  updated() {
    const event = createEvent('linkInputComms')
    window.dispatchEvent(event)
  },

  created() {
    this.fetchCommunication()
    this.fetchMentionUsernames()
    this.fetchNotificationsForCompany()
    // Event handlers
    window.eventHub.$on('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$on(
      'refreshCommunicationData',
      this.fetchNotificationsForCompany
    )
    window.eventHub.$on('resetCommunicationData', this.resetCommunicationData)
    window.eventHub.$on('resetCommunicationFilter', this.clearTypeFilter)
  },

  beforeUnmount() {
    window.eventHub.$off('refreshCommunicationData', this.fetchCommunication)
    window.eventHub.$off(
      'refreshCommunicationData',
      this.fetchNotificationsForCompany
    )
    window.eventHub.$off('resetCommunicationData', this.resetCommunicationData)
    window.eventHub.$on('resetCommunicationFilter', this.clearTypeFilter)
  },

  methods: {
    ...mapActions([
      'fetchCommunication',
      'fetchMentionUsernames',
      'fetchNotifications',
      // TODO: We should probably move the show/hide completed todos/checklist state from the store to here (it's local state)
      'toggleCompletedTodos',
      'toggleCompletedCheckLists',
      'resetCommunicationData',
      'addNewCommentRemarkId',
      'setWorkflowReviewActiveTag',
      'filterCards',
    ]),
    ...mapPiniaActions(useCommsFilterStore, ['clearTypeFilter']),
    async persistSidepaneOpenCompletedTodosState(value) {
      await persistSidepaneOpenCompletedTodosState(value)
      this.completedTodosPreferencePersisted = true
    },
    fetchNotificationsForCompany() {
      this.fetchNotifications(Silverfin.currentCompanyId)
    },
    // scrolling to anchor
    async handleAnchorScroll(cb = () => {}) {
      try {
        await waitForSidepaneAnimationEnd()

        // The awaits can outlive the anchor, and querySelector('') throws.
        const elementSelector = this.elementAnchorLink()
        if (!elementSelector) {
          return
        }

        await this.expandHiddenItems(elementSelector)

        const card = this.highlightCardBeforeScroll({
          elementId: elementSelector,
        })

        await this.scrollTo(
          elementSelector,
          rightDrawerSidepaneContainerSelector,
          getRightSidebarScrollOffset()
        )

        this.anchorElementScrolled = true
        // Only start and focus new reply when reviewing proposal
        //  not when coming from notification
        if (window.location.pathname.indexOf('proposal') !== -1) {
          this.addNewCommentRemarkId(card.id)
        }

        cb()
      } catch (e) {
        SfLog.error('SidepaneCommunication::Error scrolling to item', {
          error: e,
        })
      }
    },

    expandHiddenItems(elementSelector) {
      const id = parseInt(elementSelector.split('-').slice(-1)[0])
      return Promise.resolve().then(() => {
        const anchorElement = this.$el.querySelector(elementSelector)
        if (isVisible(anchorElement)) {
          return
        }

        // Toggle completed todos section if anchored element lives there
        else if (this.sortedCompletedTodos.find((c) => c.id === id)) {
          this.toggleCompletedTodos()
        }

        // Toggle completed checks in checklists section if anchored element lives there
        // This expands multi level completed checks(partially and fully complete checklists) as required
        else if (this.checkIds.find((c) => c === id)) {
          _.forEach(this.$refs.checkList, (checkList) => {
            if (checkList.completedCheckIds.find((c) => c === id)) {
              if (
                this.sortedCompletedCheckLists.find(
                  (c) => c.id === checkList.id
                )
              ) {
                this.toggleCompletedCheckLists()
              }
              checkList.toggleHiddenChecks()
            }
          })
        }
      })
    },
    sortRemarks(remarks) {
      return _.orderBy(remarks, ['sticky', 'lastCommentedAt'], ['desc', 'desc'])
    },
    removeTagFilter() {
      this.setWorkflowReviewActiveTag(null)
    },
  },

  mounted() {
    this.showSfAssistantChecksPane = showSfAssistantChecks()
  },
}

export default SidepaneCommunication
</script>

<style scoped lang="scss">
.sidepane-communication {
  &__changes-since-summary {
    display: flex;
  }

  &__changes-since-text {
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__changes-since-link {
    padding-left: 10px;
  }
}
</style>
